import { filter } from "lodash";
import { useState, useEffect } from "react";
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { useSelector } from "react-redux";
// components
import Page from "../../components/Page";
import Label from "../../components/Label";
import Scrollbar from "../../components/Scrollbar";
import SearchNotFound from "../../components/SearchNotFound";
import {
  UserListHead,
  AdminUserListToolbar,
  UserMoreMenu
} from "../../sections/user";
// mock
import instance from "../../api/axiosRequest";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "name", label: "Name", alignRight: false },
  { id: "email", label: "Email", alignRight: false },
  { id: "phone", label: "Phone", alignRight: false },
  { id: "date", label: "Enquiry Date", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  { id: "" }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.email.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

const validatePhone = (phone) => {
  if (phone.startsWith("+")) return phone;
  if (phone.startsWith("91")) return `+${phone}`;
  if (phone.startsWith("0")) return `+91${phone.substring(1)}`;
  return `+91${phone}`;
};

export default function Enquiry() {
  const { user } = useSelector((state) => state.auth);

  const [page, setPage] = useState(0);
  const [enquiries, setEnquiries] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [vendor, setVendor] = useState();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState("asc");

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState("name");

  const [filterName, setFilterName] = useState("");

  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const getEnquiries = async () => {
      setEnquiries([]);
      setLoading(true);
      let query = "";
      if (vendor && vendor !== "all") query = `?vendor_id=${vendor}`;
      try {
        const { data } = await instance.get(`/enquiry/getEnquiry${query}`);
        setEnquiries(data.data.rows);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setEnquiries([]);
      }
    };
    getEnquiries();
  }, [user, vendor]);

  useEffect(() => {
    const getVendors = async () => {
      setLoading(true);
      try {
        const { data } = await instance.get(`/admin/listVendors`);
        setVendorList(data.data.rows);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error.response.data);
      }
    };
    getVendors();
  }, []);

  const handleChange = (e) => {
    setVendor(e.target.value);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = enquiries.map((n) => n.email);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - enquiries.length) : 0;

  const filteredUsers = applySortFilter(
    enquiries,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  return (
    <Page title="User">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Enquiry
          </Typography>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="demo-simple-select-label">
              Vendor Category
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Vendor Category"
              defaultValue="all"
              value={vendor}
              onChange={handleChange}
            >
              <MenuItem value="all">All</MenuItem>
              {vendorList.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Card>
          <AdminUserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={enquiries.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                {!loading && (
                  <TableBody>
                    {filteredUsers
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row) => {
                        const { id, name, createdAt, email, phone } = row;
                        const isItemSelected = selected.indexOf(email) !== -1;

                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onChange={(event) => handleClick(event, email)}
                              />
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              padding="none"
                            >
                              {id}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              padding="none"
                            >
                              {name}
                            </TableCell>
                            <TableCell align="left">{email}</TableCell>
                            <TableCell align="left">{validatePhone(phone)}</TableCell>
                            <TableCell align="left">
                              {new Date(createdAt).toDateString()}
                            </TableCell>
                            <TableCell align="left">
                              <Label variant="ghost" color="success">
                                Pending
                              </Label>
                            </TableCell>

                            <TableCell align="right">
                              <UserMoreMenu row={row} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                )}

                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        {loading ? (
                          <CircularProgress />
                        ) : (
                          <SearchNotFound searchQuery={filterName} />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                {/* {!loading && <Stack sx={{width: "100%", height: 100}} justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Stack>} */}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={enquiries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
